import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-icon.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { ApiRequest } from '../../../../interface/common.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchEmspCdrFilters,
  fetchEmspCdrs,
  setSortData,
  updateEMSPCdrsFilters,
} from '../../../../redux/slices/OCPI/EMSPCdrsSlice';
import { RootState } from '../../../../redux/store';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OcpiCdrLocation {
  id?: string;
  name?: string;
  address?: string;
  city?: string;
}

interface OcpiCdr {
  id: string;
  currency?: string;
  session_id?: string;
  auth_method?: string;
  tariff_id?: string;
  cdr_location?: OcpiCdrLocation;
  total_cost?: { excl_vat?: number; incl_vat?: number };
  total_energy?: number;
  total_time?: number;
  start_date_time?: string;
  end_date_time?: string;
  last_updated?: string;
  _objectId?: string;
  _lastUpdated?: string;
  cpoCountryCode?: string;
  cpoPartyId?: string;
  _createdAt?: string;
  createdAt?: string;
  created_at?: string;
  created?: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Formats total time in hours into a human-readable duration string (e.g. 51m 20s or 1h 30m 15s).
 * @param {number | string | undefined | null} totalHours - Total time in hours.
 * @returns {string} Formatted duration string.
 */
const formatDuration = (totalHours: number | string | undefined | null): string => {
  if (totalHours == null || Number.isNaN(Number(totalHours))) {
    return '–';
  }

  const totalSeconds = Math.round(Number(totalHours) * 3600);
  if (totalSeconds <= 0) {
    return '–';
  }

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);

  return parts.join(' ') || '–';
};

// ── CDR Drill-Down Modal ─────────────────────────────────────────────────────

/**
 * Renders read-only details of a CDR received from a connected CPO.
 * @param {any} props The props.
 * @param {any} props.cdr The CDR details.
 * @param {any} props.onClose The close callback.
 */
function CdrDetailsModal({
  cdr,
  onClose,
}: Readonly<{
  cdr: OcpiCdr;
  onClose: () => void;
}>) {
  return (
    <ModalBox
      open
      header={false}
      notification
      title={null}
      closeModal={onClose}
      content={
        <div className="cpo-cdr-drilldown-modal">
          {/* Header */}
          <div className="cpo-drilldown-header">
            <div>
              <h3 className="cpo-drilldown-title">{cdr.id}</h3>
              <p className="cpo-drilldown-subtitle">
                {cdr.currency || '–'}
                {cdr.cpoCountryCode && cdr.cpoPartyId
                  ? ` · Source: ${cdr.cpoCountryCode} / ${cdr.cpoPartyId}`
                  : ''}
              </p>
            </div>
            <button type="button" onClick={onClose} className="cpo-drilldown-close">
              <CloseIcon width={30} height={30} />
            </button>
          </div>

          {/* Raw CDR detail */}
          <div className="cpo-drilldown-body">
            <pre className="cpo-cdr-drilldown-detail">{JSON.stringify(cdr, null, 2)}</pre>
          </div>
        </div>
      }
    />
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

interface CdrRow {
  id: string;
  currency: string;
  totalCost: string;
  totalEnergy: string;
  cpo: string;
  sessionId: string;
  location: string;
  authMethod: string;
  tariffId: string;
  totalTime: string;
  startDatetime: string;
  endDatetime: string;
  lastUpdated: string;
  _createdAt?: string;
  createdAt: string;
  raw: OcpiCdr;
}

/**
 * EmspCdrsPanel component displays OCPI CDRs received from connected CPOs.
 * @param {any} props The props.
 * @param {any} props.countryCode The country code.
 * @param {any} props.partyId The party ID.
 * @param {any} props.filterCountryCode Optional country code of the CPO to filter by.
 * @param {any} props.filterPartyId Optional party ID of the CPO to filter by.
 * @param {any} props.refreshTrigger Optional number that triggers a reload when incremented.
 */
export default function EmspCdrsPanel({
  countryCode,
  partyId,
  filterCountryCode,
  filterPartyId,
  refreshTrigger,
}: Readonly<{
  countryCode: string;
  partyId: string;
  filterCountryCode?: string;
  filterPartyId?: string;
  refreshTrigger?: number;
}>) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['ocpi']);
  const {
    cdrs,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
  } = useAppSelector((state: RootState) => state.emspCdrs);
  const [selectedCdr, setSelectedCdr] = useState<OcpiCdr | null>(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  /**
   * Fetches EMSP CDRs for this role with the given pagination/sort parameters.
   * @param {any} data - The pagination/sort parameters.
   */
  const fetchData = (data: ApiRequest) => {
    if (!loading) {
      dispatch(
        fetchEmspCdrs({
          countryCode,
          partyId,
          counterPartyId: filterPartyId,
          counterCountryCode: filterCountryCode,
          ...data,
        }),
      );
    }
  };

  useEffect(() => {
    dispatch(
      fetchEmspCdrs({
        countryCode,
        partyId,
        counterPartyId: filterPartyId,
        counterCountryCode: filterCountryCode,
      }),
    );
  }, [countryCode, partyId, filterPartyId, filterCountryCode, dispatch, refreshTrigger]);

  const columns = [
    {
      label: t('ocpi:created'),
      accessorKey: '_createdAt' as keyof CdrRow,
      sortAllow: false,
      /**
       * Rendering created date
       * @param {CdrRow} currentRow The current CDR row.
       * @returns {any} The rendered created date element.
       */
      customData: (currentRow: CdrRow) => (
        <span>{ConvertUTCtoDateTime(currentRow._createdAt || currentRow.createdAt)}</span>
      ),
    },
    {
      label: t('ocpi:cdrId'),
      accessorKey: 'id' as keyof CdrRow,
      sortAllow: false,
      columnWidth: 260,
      /**
       * Renders the CDR ID in a code badge.
       * @param {any} row The CDR row.
       */
      customData: (row: CdrRow) => <code className="cpo-cdr-id-badge">{row.id}</code>,
    },
    {
      label: t('ocpi:sourceCpo'),
      accessorKey: 'cpo' as keyof CdrRow,
      sortAllow: false,
      /**
       * Renders the source CPO badge.
       * @param {any} row The CDR row.
       */
      customData: (row: CdrRow) => <span className="cpo-source-cpo-badge">{row.cpo}</span>,
    },
    {
      label: t('ocpi:currency'),
      accessorKey: 'currency' as keyof CdrRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:totalCost'),
      accessorKey: 'totalCost' as keyof CdrRow,
      sortAllow: false,
      /**
       * Renders the total cost badge.
       * @param {any} row The CDR row.
       */
      customData: (row: CdrRow) => <span className="cpo-cdr-cost-badge">{row.totalCost}</span>,
    },
    {
      label: t('ocpi:energy'),
      accessorKey: 'totalEnergy' as keyof CdrRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:sessionId'),
      accessorKey: 'sessionId' as keyof CdrRow,
      sortAllow: false,
      /**
       * Renders the session ID in a code badge.
       * @param {any} row The CDR row.
       */
      customData: (row: CdrRow) => (
        <code className="cpo-cdr-id-badge">{String(row.sessionId).slice(0, 18)}</code>
      ),
    },
    {
      label: t('ocpi:location'),
      accessorKey: 'location' as keyof CdrRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:authMethod'),
      accessorKey: 'authMethod' as keyof CdrRow,
      sortAllow: false,
    },
    // {
    //   label: t('ocpi:tariffId'),
    //   accessorKey: 'tariffId' as keyof CdrRow,
    //   sortAllow: false,
    //   /**
    //    * Renders the tariff ID in a code badge.
    //    * @param {any} row The CDR row.
    //    */
    //   customData: (row: CdrRow) => (
    //     <code className="cpo-cdr-id-badge">{String(row.tariffId).slice(0, 18)}</code>
    //   ),
    // },
    {
      label: t('ocpi:totalTime'),
      accessorKey: 'totalTime' as keyof CdrRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:started'),
      accessorKey: 'startDatetime' as keyof CdrRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:ended'),
      accessorKey: 'endDatetime' as keyof CdrRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:created'),
      accessorKey: 'createdAt' as keyof CdrRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:lastUpdated'),
      accessorKey: 'lastUpdated' as keyof CdrRow,
      sortAllow: false,
    },
  ];

  const tableData: CdrRow[] = cdrs.map((cdr: OcpiCdr) => {
    const cdrId = cdr.id || cdr._objectId || '';
    const dateString = cdr.last_updated || cdr._lastUpdated;
    const lastUpdated = dateString ? new Date(dateString).toLocaleDateString() : '–';
    const startDatetime = cdr.start_date_time
      ? new Date(cdr.start_date_time).toLocaleString()
      : '–';
    const endDatetime = cdr.end_date_time ? new Date(cdr.end_date_time).toLocaleString() : '–';
    const cost = cdr.total_cost?.incl_vat ?? cdr.total_cost?.excl_vat;
    const totalCost = cost !== undefined ? `${cost} ${cdr.currency || ''}`.trim() : '–';
    const location = cdr.cdr_location?.name || cdr.cdr_location?.address || '–';
    const sourceCpo =
      cdr.cpoCountryCode && cdr.cpoPartyId ? `${cdr.cpoCountryCode} / ${cdr.cpoPartyId}` : '–';
    const createdAt =
      cdr._createdAt || cdr.createdAt || (cdr as any).created_at || (cdr as any).created || '';

    return {
      id: cdrId,
      currency: cdr.currency || '–',
      totalCost,
      totalEnergy: cdr.total_energy !== undefined ? `${cdr.total_energy} kWh` : '–',
      cpo: sourceCpo,
      sessionId: cdr.session_id || '–',
      location,
      authMethod: cdr.auth_method || '–',
      tariffId: cdr.tariff_id || '–',
      totalTime: cdr.total_time !== undefined ? formatDuration(cdr.total_time) : '–',
      startDatetime,
      endDatetime,
      lastUpdated,
      _createdAt: createdAt,
      createdAt,
      raw: cdr,
    };
  });

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageAndLimitChange = (value: ApiRequest) => {
    fetchData({ ...value, sortField: sortField ?? undefined, sortBy: sortBy ?? undefined });
  };

  /**
   * Handle the table's sort change.
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: { sortField: string; sortBy: 'asc' | 'desc' }) => {
    dispatch(setSortData(sortData));
    fetchData({
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  return (
    <div className="cpo-cdrs-panel">
      {/* ── Top bar ── */}
      <div className="cpo-cdrs-topbar">
        <div>
          <h3 className="cpo-panel-title">{t('ocpi:receivedCpoCdrs')}</h3>
          <p className="cpo-panel-subtitle">
            {cdrs.length > 0
              ? `${cdrs.length} ${t(
                  cdrs.length !== 1 ? 'ocpi:cdrCountPlural' : 'ocpi:cdrCountSingular',
                )} ${t('ocpi:receivedfromconnectedCPOs')}`
              : t('ocpi:noCdrsReceived')}
          </p>
        </div>
      </div>

      {/* ── CDRs Table ── */}
      <CustomTable
        version="v2"
        header={columns}
        data={tableData}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        filters={filters}
        filtersToDispatch={updateEMSPCdrsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchEmspCdrFilters}
        sizeChangeButtonRequired
        actionDropdown
        rowClickEvent={(_id, row) => setSelectedCdr(row.raw)}
        rowClickParamsKey="id"
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleRefresh}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('ocpi:refresh')}
      />

      {/* ── Drill-down modal ── */}
      {selectedCdr && <CdrDetailsModal cdr={selectedCdr} onClose={() => setSelectedCdr(null)} />}
    </div>
  );
}

EmspCdrsPanel.defaultProps = {
  filterCountryCode: undefined,
  filterPartyId: undefined,
  refreshTrigger: undefined,
};
